(function () {
	const FINCH_SECTION = ".main-product-finch";
	const KBITE_SELECTOR = ".gw-button-widget";
	const POST_FETCH_SYNC_MS = [50, 300, 800, 1200, 1800];
	const sectionState = new WeakMap();

	function getFinchSection(root) {
		if (!root) {
			return null;
		}

		if (root.matches?.(FINCH_SECTION)) {
			return root;
		}

		return root.querySelector?.(FINCH_SECTION) || root.closest?.(FINCH_SECTION);
	}

	function getState(section) {
		if (!sectionState.has(section)) {
			sectionState.set(section, {
				isSyncing: false,
				syncTimer: null,
				reinjectTimer: null,
				postFetchTimers: [],
				kbiteTemplate: null,
			});
		}

		return sectionState.get(section);
	}

	function getVariantFromPicker(picker, variantId) {
		if (picker?.currentVariant) {
			return picker.currentVariant;
		}

		if (!picker || !variantId) {
			return null;
		}

		try {
			const dataEl = picker.querySelector("[data-all-variants-no-high]");
			if (!dataEl) {
				return null;
			}

			const variants = JSON.parse(dataEl.textContent);
			return (
				variants.find((variant) => String(variant.id) === String(variantId)) ||
				null
			);
		} catch (error) {
			return null;
		}
	}

	function findKbiteWidget(finchSection) {
		return finchSection?.querySelector(KBITE_SELECTOR) || null;
	}

	function getKbiteInsertTarget(finchSection) {
		return (
			finchSection.querySelector(".product-form__buttons-row") ||
			finchSection.querySelector(".product-form__buttons") ||
			null
		);
	}

	function nodeContainsKbite(node) {
		return (
			node?.nodeType === 1 &&
			(node.matches?.(KBITE_SELECTOR) || Boolean(node.querySelector?.(KBITE_SELECTOR)))
		);
	}

	function cacheKbiteWidget(finchSection, widget) {
		if (widget) {
			getState(finchSection).kbiteTemplate = widget.outerHTML;
		}
	}

	function restoreKbiteWidget(finchSection, variant) {
		const state = getState(finchSection);

		if (!state.kbiteTemplate || findKbiteWidget(finchSection)) {
			return findKbiteWidget(finchSection);
		}

		const target = getKbiteInsertTarget(finchSection);
		if (!target) {
			return null;
		}

		target.insertAdjacentHTML("beforeend", state.kbiteTemplate);
		const widget = findKbiteWidget(finchSection);

		if (widget && variant?.id) {
			widget.dataset.variantId = String(variant.id);
		}

		return widget;
	}

	function syncKbiteWidget(finchSection, variant) {
		if (!variant) {
			return;
		}

		const productForm = finchSection.querySelector("product-form");
		const variantInput = productForm?.querySelector('input[name="id"]');

		if (variantInput) {
			variantInput.disabled = false;

			if (variant.id) {
				variantInput.value = String(variant.id);
			}
		}

		let widget = findKbiteWidget(finchSection);

		if (widget && variant.id) {
			widget.dataset.variantId = String(variant.id);
			cacheKbiteWidget(finchSection, widget);
			return;
		}

		if (variant.available === false) {
			widget = restoreKbiteWidget(finchSection, variant);

			if (widget) {
				cacheKbiteWidget(finchSection, widget);
				return;
			}

			requestKbiteReinject(finchSection);
		}
	}

	function requestKbiteReinject(finchSection) {
		const variantInput = finchSection.querySelector('input[name="id"]');
		if (!variantInput || findKbiteWidget(finchSection)) {
			return;
		}

		const picker = finchSection.querySelector("variant-radios, variant-selects");
		const variant = getVariantFromPicker(picker, variantInput.value);

		if (variant?.available !== false) {
			return;
		}

		const state = getState(finchSection);
		clearTimeout(state.reinjectTimer);

		state.reinjectTimer = window.setTimeout(() => {
			if (findKbiteWidget(finchSection)) {
				return;
			}

			variantInput.dispatchEvent(new Event("change", { bubbles: true }));
			variantInput.dispatchEvent(new Event("input", { bubbles: true }));
		}, 100);
	}

	function syncKbiteSection(section) {
		const finchSection = getFinchSection(section);
		if (!finchSection) {
			return;
		}

		const state = getState(finchSection);
		if (state.isSyncing) {
			return;
		}

		state.isSyncing = true;

		try {
			const productForm = finchSection.querySelector("product-form");
			const variantInput = productForm?.querySelector('input[name="id"]');
			if (!variantInput) {
				return;
			}

			const picker = finchSection.querySelector("variant-radios, variant-selects");
			const variant = getVariantFromPicker(picker, variantInput.value);
			syncKbiteWidget(finchSection, variant);
		} finally {
			state.isSyncing = false;
		}
	}

	function scheduleKbiteSync(section) {
		const finchSection = getFinchSection(section);
		if (!finchSection) {
			return;
		}

		const state = getState(finchSection);

		clearTimeout(state.syncTimer);
		state.postFetchTimers.forEach((timerId) => window.clearTimeout(timerId));
		state.postFetchTimers = [];

		state.syncTimer = window.setTimeout(() => {
			syncKbiteSection(finchSection);
		}, 0);

		POST_FETCH_SYNC_MS.forEach((delay) => {
			const timerId = window.setTimeout(() => {
				syncKbiteSection(finchSection);
			}, delay);
			state.postFetchTimers.push(timerId);
		});
	}

	function observeKbiteWidget(finchSection) {
		if (finchSection.dataset.kbiteObserverBound === "true") {
			return;
		}

		const productForm = finchSection.querySelector("product-form");
		if (!productForm) {
			return;
		}

		finchSection.dataset.kbiteObserverBound = "true";

		const observer = new MutationObserver((mutations) => {
			const state = getState(finchSection);
			if (state.isSyncing) {
				return;
			}

			let widgetAdded = false;
			let widgetRemoved = false;

			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (nodeContainsKbite(node)) {
						widgetAdded = true;
					}
				});

				mutation.removedNodes.forEach((node) => {
					if (nodeContainsKbite(node)) {
						widgetRemoved = true;
					}
				});
			});

			if (widgetAdded) {
				const widget = findKbiteWidget(finchSection);
				cacheKbiteWidget(finchSection, widget);
				scheduleKbiteSync(finchSection);
				return;
			}

			if (widgetRemoved) {
				scheduleKbiteSync(finchSection);
			}
		});

		observer.observe(productForm, {
			childList: true,
			subtree: true,
		});
	}

	function patchVariantSelectsForKbite() {
		const variantRadios = customElements.get("variant-radios");
		if (!variantRadios) {
			return false;
		}

		const proto = Object.getPrototypeOf(variantRadios.prototype);
		if (!proto || proto.__kbiteFinchPatched) {
			return true;
		}

		const originalToggleAddButton = proto.toggleAddButton;
		if (typeof originalToggleAddButton !== "function") {
			return false;
		}

		proto.toggleAddButton = function (disable, text) {
			originalToggleAddButton.call(this, disable, text);

			const finchSection = getFinchSection(this);
			if (finchSection) {
				scheduleKbiteSync(finchSection);
			}
		};

		proto.__kbiteFinchPatched = true;
		return true;
	}

	function initKbiteFinch(root) {
		const finchSection = getFinchSection(root || document);
		if (!finchSection) {
			return;
		}

		patchVariantSelectsForKbite();
		observeKbiteWidget(finchSection);

		const widget = findKbiteWidget(finchSection);
		cacheKbiteWidget(finchSection, widget);
		scheduleKbiteSync(finchSection);
	}

	function handleVariantChange(event) {
		const sectionId = event?.data?.sectionId;
		if (!sectionId) {
			return;
		}

		scheduleKbiteSync(document.getElementById(`shopify-section-${sectionId}`));
	}

	document.addEventListener("DOMContentLoaded", function () {
		if (!patchVariantSelectsForKbite()) {
			window.setTimeout(patchVariantSelectsForKbite, 0);
		}

		initKbiteFinch(document);

		if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
			subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
		}
	});

	document.addEventListener(
		"change",
		function (event) {
			const finchSection = getFinchSection(event.target);
			if (!finchSection) {
				return;
			}

			if (
				event.target.matches('input[name="id"]') ||
				event.target.closest("variant-radios, variant-selects")
			) {
				scheduleKbiteSync(finchSection);
			}
		},
		true,
	);

	document.addEventListener("shopify:section:load", function (event) {
		patchVariantSelectsForKbite();
		initKbiteFinch(event.target || document);
		scheduleKbiteSync(document);
	});

	window.syncKbiteForFinch = scheduleKbiteSync;
})();
